import React, { forwardRef } from 'react'
import uniqueId from 'lodash.uniqueid'
import clsx from 'clsx'
import { Checkbox as CheckboxStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps } from '../helperTypes'

type Props = {
  /** If `true`, the component is checked */
  checked?: boolean
  /** The default checked status for an uncontrolled component */
  defaultChecked?: boolean
  /**
   * The id that will be passed to the underlying input element.
   */
  id?: string
  /** Indicates that checkbox is in indeterminate state */
  indeterminate?: boolean
  /** The label to show next to the `<input />` */
  label?: string
  /**
   * props will be spread in internal label element
   * @default {}
   */
  labelProps?: {}
  /** The callback for when the input's state changes */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
  /** mouse click handler */
  onClick?: React.MouseEventHandler<HTMLInputElement>
  /**
   * The size of the checkbox
   * @default medium
   */
  size?: 'medium' | 'large'
  /**
   * props passed to root component of checkbox (ie) div
   * @default {}
   */
  rootProps?: {}
}

type DeprecatedProps = {
  large?: boolean
}

export type CheckboxProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: CheckboxProps) => {
  const { large, ...rest } = props
  return {
    ...(large && { size: 'large' }),
    ...rest,
  }
}

const useStyles = createUseStyles(CheckboxStyles, 'checkbox')

export const Checkbox = (props: CheckboxProps, ref: React.Ref<HTMLInputElement>) => {
  const {
    id: propsId,
    label,
    size,
    indeterminate,
    className,
    onChange,
    labelProps = {},
    style,
    rootProps = {},
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps('Checkbox', { large: 'size' }, props)
  const defaultId = React.useRef<string>(uniqueId('Checkbox-'))
  const id = propsId || defaultId.current
  const classes = useStyles()

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    onChange && onChange(evt, evt.target.checked)
  }

  return (
    <div
      className={clsx(className, classes.root, indeterminate && classes._indeterminate, size && classes[`_${size}`])}
      style={style}
      onClick={(evt) => evt.stopPropagation()}
      {...rootProps}
    >
      <input
        className={classes.input}
        ref={ref}
        id={id}
        type='checkbox'
        onChange={handleChange}
        aria-checked={indeterminate ? 'mixed' : rest.checked || rest.defaultChecked}
        {...rest}
      />
      <label className={classes.label} htmlFor={id} {...labelProps}>
        {label}
      </label>
    </div>
  )
}

export default forwardRef<HTMLInputElement, CheckboxProps>(Checkbox)
